import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronsUpDown, Image as ImageIcon, Loader2, X } from 'lucide-react';
import type { ProductToken } from '@sleed/bizcommerce-api';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { cn } from '@/lib/utils';
import { productsApi } from '~/lib/api/client';

const DEBOUNCE_MS = 300;
const PAGE_SIZE = 20;

/** What the trigger shows for the current value. */
export interface ProductComboboxValue {
	id: number;
	cd?: string | null;
	nm?: string | null;
	img?: string | null;
}

interface Props {
	value: ProductComboboxValue | null;
	onChange: (product: ProductToken) => void;
	/** Shows a clear (×) button when set and a value is selected. */
	onClear?: () => void;
	placeholder?: string;
	className?: string;
	disabled?: boolean;
}

export function ProductThumb({ src, alt, size = 32 }: { src?: string | null; alt?: string | null; size?: number }) {
	return src ? (
		<img src={src} alt={alt ?? ''} style={{ width: size, height: size }} className="shrink-0 rounded border border-border object-contain" />
	) : (
		<div
			style={{ width: size, height: size }}
			className="flex shrink-0 items-center justify-center rounded border border-border bg-elevated">
			<ImageIcon size={Math.round(size / 2.4)} className="text-foreground-disabled" />
		</div>
	);
}

/**
 * Single-product async search (code, name, thumbnail) over the catalogue lookup — the same endpoint the
 * wishlist/order pickers use, as a combobox so it fits inline in a table row or a toolbar.
 */
export function ProductCombobox({ value, onChange, onClear, placeholder, className, disabled }: Props) {
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState('');
	const [results, setResults] = useState<ProductToken[]>([]);
	const [loading, setLoading] = useState(false);
	// Latest-query guard so a slow earlier response can't overwrite a newer one.
	const requestSeq = useRef(0);

	useEffect(() => {
		if (!open) return;
		const term = query.trim();
		if (!term) {
			setResults([]);
			setLoading(false);
			return;
		}
		let cancelled = false;
		const seq = ++requestSeq.current;
		setLoading(true);
		const handle = setTimeout(() => {
			productsApi
				.lookup({ filter: { term, isEditAction: true }, pagination: { page: 1, pageSize: PAGE_SIZE } })
				.then(res => {
					if (!cancelled && seq === requestSeq.current) setResults(res.data ?? []);
				})
				.catch(() => {
					if (!cancelled && seq === requestSeq.current) setResults([]);
				})
				.finally(() => {
					if (!cancelled && seq === requestSeq.current) setLoading(false);
				});
		}, DEBOUNCE_MS);
		return () => {
			cancelled = true;
			clearTimeout(handle);
		};
	}, [query, open]);

	return (
		<div className={cn('flex min-w-0 items-center gap-1', className)}>
			<Popover open={open} onOpenChange={setOpen}>
				<PopoverTrigger asChild disabled={disabled}>
					<button
						type="button"
						className="flex h-9 min-w-0 flex-1 items-center justify-between gap-2 rounded-md border border-border bg-surface px-2 text-left text-sm text-foreground disabled:cursor-not-allowed disabled:opacity-50">
						{value ? (
							<span className="flex min-w-0 items-center gap-2">
								<ProductThumb src={value.img} alt={value.nm} size={24} />
								{value.cd && <span className="shrink-0 font-mono text-[11px] text-foreground-muted">{value.cd}</span>}
								<span className="truncate">{value.nm || `#${value.id}`}</span>
							</span>
						) : (
							<span className="truncate text-foreground-subtle">{placeholder ?? t('catalog.bundles.pickProduct')}</span>
						)}
						<ChevronsUpDown size={14} className="shrink-0 text-foreground-subtle" />
					</button>
				</PopoverTrigger>
				<PopoverContent className="w-[420px] p-0" align="start">
					<Command shouldFilter={false}>
						<CommandInput value={query} onValueChange={setQuery} placeholder={t('catalog.bundles.searchProduct')} />
						<CommandList>
							{loading ? (
								<div className="flex items-center justify-center gap-2 py-6 text-sm text-foreground-muted">
									<Loader2 size={14} className="animate-spin" /> {t('common.loading')}
								</div>
							) : (
								<>
									<CommandEmpty>{query.trim() ? t('common.noResults') : t('catalog.bundles.searchHint')}</CommandEmpty>
									<CommandGroup>
										{results.map(product => (
											<CommandItem
												key={product.id}
												value={String(product.id)}
												onSelect={() => {
													if (product.id == null) return;
													onChange(product);
													setOpen(false);
												}}>
												<ProductThumb src={product.img1} alt={product.nm} size={28} />
												<span className="shrink-0 font-mono text-[11px] text-foreground-muted">{product.cd}</span>
												<span className="min-w-0 flex-1 truncate">{product.nm}</span>
											</CommandItem>
										))}
									</CommandGroup>
								</>
							)}
						</CommandList>
					</Command>
				</PopoverContent>
			</Popover>
			{onClear && value && !disabled && (
				<button
					type="button"
					onClick={onClear}
					aria-label={t('catalog.bundles.clearProduct')}
					className="rounded p-1 text-foreground-subtle hover:bg-elevated hover:text-foreground">
					<X size={14} />
				</button>
			)}
		</div>
	);
}
